import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Github, Linkedin, Mail, Heart } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Footer component with legal links and social media
 * Required for GDPR compliance
 */

const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.surface};
  border-top: 2px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing[12]} ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[20]};
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: ${props => props.theme.spacing[12]};
  margin-bottom: ${props => props.theme.spacing[10]};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing[8]};
  }
`;

const FooterSection = styled.div``;

const FooterTitle = styled.h3`
  font-size: ${props => props.theme.typography.sizes.lg};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing[4]};
`;

const FooterDescription = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  line-height: ${props => props.theme.typography.lineHeights.relaxed};
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing[3]};
`;

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.theme.colors.neutral[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  
  &:hover {
    background-color: ${props => props.theme.colors.primary[600]};
    color: white;
    transform: translateY(-2px);
  }
  
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary[600]};
    outline-offset: 2px;
  }
`;

const FooterLinks = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[3]};
`;

const FooterLink = styled(Link)`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: color ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  
  &:hover {
    color: ${props => props.theme.colors.primary[600]};
  }
  
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary[600]};
    outline-offset: 2px;
    border-radius: ${props => props.theme.borderRadius.sm};
  }
`;

const FooterBottom = styled.div`
  padding-top: ${props => props.theme.spacing[8]};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing[4]};
    text-align: center;
  }
`;

const Copyright = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const MadeWith = styled.p`
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const Footer: React.FC = () => {
  const { language } = useLanguage();
  
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { 
      icon: Github, 
      href: 'https://github.com/adda-neb', 
      label: 'GitHub',
      ariaLabel: language === 'fr' ? 'Visiter mon profil GitHub' : 'Visit my GitHub profile'
    },
    { 
      icon: Linkedin, 
      href: 'https://linkedin.com/in/nadir-benadda-7741b1271/', 
      label: 'LinkedIn',
      ariaLabel: language === 'fr' ? 'Visiter mon profil LinkedIn' : 'Visit my LinkedIn profile'
    },
    { 
      icon: Mail, 
      href: 'mailto:n.benadda.dev@gmail.com', 
      label: 'Email',
      ariaLabel: language === 'fr' ? 'M\'envoyer un email' : 'Send me an email'
    },
  ];

  return (
    <FooterContainer role="contentinfo">
      <FooterContent>
        <FooterTop>
          <FooterSection>
            <FooterTitle>Nadir</FooterTitle>
            <FooterDescription>
              {language === 'fr'
                ? 'Développeur Web Full-Stack passionné par les technologies modernes et l\'intelligence artificielle. Créer des solutions innovantes est ma spécialité.'
                : 'Full-Stack Web Developer passionate about modern technologies and artificial intelligence. Creating innovative solutions is my specialty.'}
            </FooterDescription>
            <SocialLinks>
              {socialLinks.map((social) => (
                <SocialLink
                  key={social.label}
                  href={social.href}
                  target={social.href.startsWith('mailto:') ? undefined : '_blank'}
                  rel={social.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                  aria-label={social.ariaLabel}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon size={20} aria-hidden="true" />
                </SocialLink>
              ))}
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>
              {language === 'fr' ? 'Navigation' : 'Navigation'}
            </FooterTitle>
            <FooterLinks>
              <FooterLink to="/#about">
                {language === 'fr' ? 'À propos' : 'About'}
              </FooterLink>
              <FooterLink to="/#projects">
                {language === 'fr' ? 'Projets' : 'Projects'}
              </FooterLink>
              <FooterLink to="/#tools">
                {language === 'fr' ? 'Outils' : 'Tools'}
              </FooterLink>
              <FooterLink to="/#resources">
                {language === 'fr' ? 'Ressources' : 'Resources'}
              </FooterLink>
              <FooterLink to="/#contact">
                {language === 'fr' ? 'Contact' : 'Contact'}
              </FooterLink>
            </FooterLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>
              {language === 'fr' ? 'Légal' : 'Legal'}
            </FooterTitle>
            <FooterLinks>
              <FooterLink to="/mentions-legales">
                {language === 'fr' ? 'Mentions Légales' : 'Legal Notice'}
              </FooterLink>
              <FooterLink to="/politique-confidentialite">
                {language === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'}
              </FooterLink>
            </FooterLinks>
          </FooterSection>
        </FooterTop>

        <FooterBottom>
          <Copyright>
            © {currentYear} Nadir Benadda.{' '}
            {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
          </Copyright>
          <MadeWith>
            {language === 'fr' ? 'Fait avec' : 'Made with'}{' '}
            <Heart size={16} fill="currentColor" color="#ef4444" aria-hidden="true" />{' '}
            {language === 'fr' ? 'et React' : 'and React'}
          </MadeWith>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
